import { clientWithApiClient } from "@fm/api/client";
import type { ApiClient } from "@fm/api/types";
import { add, format } from "date-fns";
import { getApiBaseUrl } from "../lib/config";

export interface RatePlanGridResponse {
  data: Array<{
    entity_id: string;
    entity_type: "vehicle";
    entity: {
      id: string;
      name: string;
      description: string;
      status: string;
    };
    dates: Array<{
      date: string; // YYYY-MM-DD
      best_rate_plan: RatePlanFromGrid;
      alternative_rate_plans: RatePlanFromGrid[];
    }>;
  }>;
  meta: {
    from_date: string;
    to_date: string;
    guests: {
      adults: number;
      children: number;
      infants: number;
      pets: number;
    };
    total_entities: number;
    date_range_days: number;
  };
}

export interface RatePlanFromGrid {
  id: string;
  name: string;
  base_rate: string;
  rate_type: string;
  priority: number;
  calculated_price: string;
  price_breakdown: {
    base_rate: string;
    guest_adjustments: Array<{
      guest_type: "adults" | "children" | "infants" | "pets";
      quantity: number;
      rate_per_guest: string;
      total_adjustment: string;
      description?: string;
    }>;
    total_adjustments: string;
    final_price: string;
  };
  rate_modifiers: Array<{
    id: string;
    guest_type: string;
    quantity_threshold: number;
    max_quantity: number;
    adjustment_type: string;
    adjustment_value: string;
  }>;
  pricing_rules: Array<{
    id: string;
    rule_type: string;
    key: string;
    value: string;
    priority: number;
  }>;
  currency?: string;
  start_date?: string;
  end_date?: string;
  base_adults_included?: number;
  base_children_included?: number;
  base_infants_included?: number;
  base_pets_included?: number;
}

export interface UseRatePlansOptions {
  apiKey: string;
  fleetId: string;
  apiBaseUrl?: string;
}

export interface UseRatePlansReturn {
  getRatePlansGrid: (params: {
    vehicleId: string;
    startDate: string;
    endDate: string;
    guests: {
      adults: number;
      children: number;
      infants: number;
      pets: number;
    };
  }) => Promise<RatePlanGridResponse>;
  calculateDailyRates: (params: {
    vehicleId: string;
    startDate: string;
    endDate: string;
    guests: {
      adults: number;
      children: number;
      infants: number;
      pets: number;
    };
  }) => Promise<
    Array<{
      date: string;
      quoted_amount: string;
      rate_plan_id: string;
      notes: string;
    }>
  >;
}

export function useRatePlans(options: UseRatePlansOptions): UseRatePlansReturn {
  const { apiKey, fleetId, apiBaseUrl } = options;

  // Create API client with the provided apiKey
  const apiClient: ApiClient = {
    baseUrl: getApiBaseUrl(apiBaseUrl),
    apiKey,
  };

  const getRatePlansGrid = async (params: {
    vehicleId: string;
    startDate: string;
    endDate: string;
    guests: {
      adults: number;
      children: number;
      infants: number;
      pets: number;
    };
  }) => {
    try {
      const searchParams = new URLSearchParams({ fleet_id: fleetId });

      const from = params.startDate;
      const to = format(
        add(new Date(params.endDate + "T00:00:00"), { days: -1 }),
        "yyyy-MM-dd",
      );

      const response = await clientWithApiClient<RatePlanGridResponse>(
        apiClient,
        `/rate-plans/grid?${searchParams}`,
        {
          method: "POST",
          body: JSON.stringify({
            vehicle_ids: [params.vehicleId],
            from_date: from,
            to_date: to,
            guests: params.guests,
          }),
        },
      );

      return response;
    } catch (error) {
      console.error("Error fetching rate plans grid:", error);
      throw error;
    }
  };

  const calculateDailyRates = async (params: {
    vehicleId: string;
    startDate: string;
    endDate: string;
    guests: {
      adults: number;
      children: number;
      infants: number;
      pets: number;
    };
  }) => {
    // Use the same approach as the existing QuoteWizard
    // Pass dates exactly as they are (pick_up inclusive, return exclusive)
    const gridResponse = await getRatePlansGrid(params);

    if (!gridResponse.data || gridResponse.data.length === 0) {
      throw new Error("No rate plans found for the selected vehicle and dates");
    }

    const vehicleData = gridResponse.data[0];
    if (!vehicleData) {
      throw new Error("Vehicle data not found in rate plans response");
    }

    // Calculate daily rates from the grid response
    // The grid response should already contain the correct date range
    const dailyRates = vehicleData.dates.map((dateData) => ({
      date: dateData.date,
      quoted_amount: dateData.best_rate_plan.calculated_price,
      rate_plan_id: dateData.best_rate_plan.id,
      notes: `Day ${dateData.date}`,
    }));

    return dailyRates;
  };

  return {
    getRatePlansGrid,
    calculateDailyRates,
  };
}
